import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { parseOldUnits, toRuleSet } from "@common-components/schedule-picker/recurrence-utils";
import { createLeanEventReuser } from './stable-lean-events';
import { expandRecurringOccurrences } from "./recurring-occurrence-expansion";
import { hasUsableRecurrenceRule } from "./recurrence-rule-guard";
import { isDripStepSkippedForOccurrence, type PendingStepSkipEntry } from "./recurring-occurrence-filter";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { Session } from "@common-models/session";
import { boardsSelectByQuery, campaignsSelectByQuery, dripsSelectByQuery, dripsSelectOneObjectByQuery, sessionSelectOneObjectByQuery, userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { buildBoardNameMap, resolveGroupNames } from "@common-reducers/calendar-audience-names";
import { resolveBaseSendAt, projectFollowupSendDates, isUpcomingCampaignSend, projectDripCalendarOccurrences } from "../../extension/context/content/components/campaigns-redesign/campaign-schedule";
import { markPausedDripEvents, pausedDripIdSet } from "@common/views/calendar/paused-event";
import { defaultEventcolor } from "@common/views/calendar/message-color";
import { WaContact } from "@common/models/waContact";
import { CommonRootState } from "@common/types/common-root-state-type";
import { sortContactsByGroupThenName } from "@common/utils/contact-sort-utils";
import { getWaContactBySerializedId } from "./wa-contact-entity-lookup";
import { createSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { RRule, RRuleSet } from "rrule";
import { selectCurrentUserSession, selectUserId, selectUserObject } from "./UserSelectors";
import { Campaign } from "../models/campaign-model";
import { RecurrenceUnit } from "../types/interface";
import { debugLog } from "@common/utils/debug-logger";
import { getEffectiveMessageStatus } from "@common-components/message-status/message-status-config";
import {
    campaignOutcomeOf,
    campaignsVisible,
    isSentRecord,
    messageMatchesPlannerFilters,
    normalizePlannerFilters,
    plannerSourceStatuses,
    type PlannerFilters,
} from "@common/services/planner-filters";
import {
    buildFailedCampaignGroupEvents,
    buildSentCampaignEvents,
    campaignChipOutcome,
    currentStepDueFromCompleted,
    foldCampaignFailures,
    mergeCampaignDocs,
} from "./planner-campaign-sends";

// Calendar event-processing instrumentation. These run inside reselect combiners
// and equality fns (i.e. on every recompute during calendar interaction), so they
// are gated behind localStorage BT_DEBUG=calendar-selectors (or BT_DEBUG=1) rather
// than logged unconditionally. See @common/utils/debug-logger.
const clog = (...args: unknown[]) => debugLog('calendar-selectors', ...args);

/**
 * Selector to get the current month start string from the calendar state
 * This ensures all components use the same month string format
 */
export const getViewMonthStartStr = createSelector(
    [(state: CommonRootState) => state.CalendarReducer.currentDate],
    (currentDate) => {
        return moment(currentDate).startOf("month").toISOString();
    }
);

/**
 * Custom equality function for comparing arrays of events
 * This is more efficient than deep comparison for large arrays
 */
export const eventsEqualityFn = (prev: UserMessage[], next: UserMessage[]): boolean => {
    // Always return false during initial load to ensure we get events
    if (!prev || !next) return false;

    // If the arrays have different lengths, they're definitely different
    if (prev.length !== next.length) {
        clog(`Events length changed: ${prev.length} -> ${next.length}`);
        return false;
    }

    // If both arrays are empty, they're equal
    if (prev.length === 0 && next.length === 0) return true;

    // Check a sample of events instead of the entire array
    // This is more efficient for large arrays
    const sampleSize = Math.min(5, prev.length);
    const sampleIndices = [0, prev.length - 1];

    // Add some indices in the middle for larger arrays
    if (prev.length > 10) {
        sampleIndices.push(
            Math.floor(prev.length / 4),
            Math.floor(prev.length / 2),
            Math.floor(3 * prev.length / 4)
        );
    }

    // Check the sample events
    for (let i = 0; i < Math.min(sampleSize, sampleIndices.length); i++) {
        const idx = sampleIndices[i];
        if (prev[idx]?.id !== next[idx]?.id ||
            prev[idx]?.dueDate !== next[idx]?.dueDate ||
            prev[idx]?.messageColor !== next[idx]?.messageColor) {
            clog(`Events changed at index ${idx}`);
            return false;
        }
    }

    // If all samples match, consider the arrays equal
    return true;
};

export const hasActiveSocketSession = createSelector(
    [
        (state: CommonRootState) => state,
        (state: CommonRootState) => selectUserId(state),
    ],
    (state: CommonRootState, userId) => {
        const session = sessionSelectOneObjectByQuery(state.DBSessionReducer, {
            status: { $in: ["connected"] },
            owner: userId
        });
        return !!session;
    }
);


export const getIsContactChecked = createSelector(
    [
        (state: CommonRootState, wid) => state,
        (state: CommonRootState, wid) => wid,
    ],
    (state: CommonRootState, wid) => {
        const importedContactsArray = state.WhatsAppReducer.importedContactsArray

        const isChecked = importedContactsArray?.filter((contact: { whatsappId?: string }) => contact?.whatsappId === wid).length > 0

        return isChecked
    }
);

export const calculatedWaContactName = (waContact: any): string => {
    const fullName = [waContact?.firstName, waContact?.lastName].filter(Boolean).join(' ');
    return waContact?.name || waContact?.displayName || waContact?.shortName || fullName || waContact?.mobile;
}
export const extractPhoneNumber = (id: string, withoutPlus?: boolean): string => {
    if (typeof id !== 'string') return "";

    const phoneNumber = id.split('@')[0];
    if (!phoneNumber) return '';
    
    if (withoutPlus) {
        // Remove + if present and withoutPlus is requested
        return phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;
    } else {
        // Add + only if not already present
        return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    }
};



export const getWaContactDisplayLabel = (contact: any): string => {
    // Extract the serialized ID from various possible locations
    const serializedId = contact?.id?._serialized
        || (typeof contact?.id === 'string' ? contact.id : null)
        || contact?.waId
        || contact?._serialized
        || contact?._id;

    return contact?.formattedTitle
        ?? calculatedWaContactName(contact)
        ?? contact?.waName
        ?? contact?.displayName
        ?? contact?.notifyName
        ?? contact?.notify
        ?? contact?.verifiedName
        ?? contact?.pushname
        ?? extractPhoneNumber(serializedId);
}

export const selectContactLabelByWhatsappId = createSelector(
    [
        (state: CommonRootState) => state,
        (state: CommonRootState, whatsappId: string) => whatsappId,
    ],
    (state: CommonRootState, whatsappId: string) => {
        const contact = selectWhatsappContactById(state, whatsappId);
        return getWaContactDisplayLabel(contact);
    }
);

// Selector to select a whatsapp contact by id (shape-agnostic O(1) lookup)
export const selectWhatsappContactById = createSelector(
    [
        (state: CommonRootState, whatsappId: string) => whatsappId,
        (state: CommonRootState) => state.DBWaContactsCacheReducer.entities,
    ],
    (whatsappId: string, entities) => {
        return getWaContactBySerializedId(entities, whatsappId) ?? undefined;
    }
);

// Selector to select a whatsapp contact name by id
export const selectWhatsappContactName = createSelector(
    [
        selectWhatsappContactById,
    ],
    (contact) => {
        //clog('contact', contact)
        return calculatedWaContactName(contact);
    }
);

export const selectIsGroup = createSelector(
    [
        (whatsappId: string) => whatsappId,
    ],
    (whatsappId: string) => {
        return chatKindOf(whatsappId) === 'group';
    }
)

// Selector to select a profile picture by id.
// Shape-agnostic lookup (extension lean `chatId` shape / website DB shape) with
// the CalendarReducer.contactPicMap fallback that fetchAndCacheProfilePicUrlsThunk
// fills for contacts that never enter the cache (e.g. campaign View-logs rows).
export const selectProfilePic = createSelector(
    [
        (state: CommonRootState) => state.DBWaContactsCacheReducer.entities,
        (state: CommonRootState) => state.CalendarReducer?.contactPicMap,
        (state: CommonRootState, whatsappId: string) => whatsappId,
    ],
    (entities, contactPicMap, whatsappId: string) => {
        if (!whatsappId) return undefined;
        const contact = getWaContactBySerializedId(entities, whatsappId);
        return contact?.imgUrl || contactPicMap?.[whatsappId] || undefined;
    }
);


export const getIsSocketConnectedToWhatsApp = createSelector(
    [
        (state: CommonRootState) => selectCurrentUserSession(state),
    ],
    (session: Session | undefined) => {
        return session?.active && session?.status === "connected";
    }
)

/**
 * Selector to check if messages are currently loading
 */
export const getIsMessagesLoading = createSelector(
    [(state: CommonRootState) => state.DBUserMessageReducer?.itemsLoading],
    (loading) => {
        return loading === true;
    }
);

// Create a more specific selector for messages to reduce unnecessary recalculations
export const getFilteredMessages = createSelector(
    [
        (state: CommonRootState) => state.DBUserMessageReducer,
        (state: CommonRootState) => getIsMessagesLoading(state)
    ],
    (dbUserMessageReducer, isLoading) => {
        // Add debug logging to see if this selector is being called
        clog('getFilteredMessages called - input changed, loading:', isLoading);

        // If messages are loading, return empty array to prevent calculations with partial data
        if (isLoading) {
            clog('Messages are loading, returning empty array');
            return [];
        }

        // Check if the reducer is properly initialized
        if (!dbUserMessageReducer || !dbUserMessageReducer.entities) {
            console.warn('DBUserMessageReducer not properly initialized');
            return [];
        }

        const messages = userMessagesSelectByQuery(dbUserMessageReducer, {
            $or: [{ deleted: { $exists: false } }, { deleted: false }],
            status: 'pending',
        });
        clog(`Found ${messages.length} filtered messages`);

        // Log some sample messages for debugging
        if (messages.length > 0) {
            clog('Sample message:', JSON.stringify(messages[0], null, 2));
        }

        return messages;
    }
);

// Create a selector for user preferences to avoid recalculations when only messages change
export const getUserCalendarPreferences = createSelector(
    [(state: CommonRootState) => selectUserObject(state)],
    (selectedUser) => {
        // Add debug logging
        clog('getUserCalendarPreferences called - input changed');
        // Week start used by the calendar grid (moment `dow`, 0 = Sunday) — see
        // calendar-view.tsx `moment.updateLocale('en', { week: { dow: startDayAtWeek }})`.
        // The data range MUST use the same value so events on adjacent-month overflow
        // days (e.g. June 3 shown in May's last row) are included.
        const calendarStartDayFromDB = (selectedUser?.preferences?.calendarStartDay ?? 0) % 7;
        return { calendarStartDayFromDB };
    }
);

// Create a memoized selector for the date range to avoid recalculations
export const getDateRange = createSelector(
    [
        (state: CommonRootState) => getViewMonthStartStr(state),
        (state: CommonRootState) => getUserCalendarPreferences(state).calendarStartDayFromDB,
    ],
    (viewMonthStartStr, calendarStartDayFromDB) => {
        clog('getDateRange called - input changed:', viewMonthStartStr);
        const monthStart = moment(viewMonthStartStr).startOf("month");
        const monthEnd = moment(viewMonthStartStr).endOf("month");

        // Calculate view range boundaries
        const diffStart = (monthStart.day() - calendarStartDayFromDB + 7) % 7;
        const rangeStart = monthStart.clone().subtract(diffStart, "days").utc().toDate();

        const lastDayOfWeek = (calendarStartDayFromDB + 6) % 7;
        const diffEnd = (lastDayOfWeek - monthEnd.day() + 7) % 7;
        const rangeEnd = monthEnd.clone().add(diffEnd, "days").utc().toDate();

        return {
            rangeStart,
            rangeEnd,
            rangeStartTime: rangeStart.getTime(),
            rangeEndTime: rangeEnd.getTime()
        };
    }
);

// Selector for non-recurring events
export const getNonRecurringEvents = createSelector(
    [
        (state: CommonRootState) => getFilteredMessages(state),
        (state: CommonRootState) => getDateRange(state),
    ],
    (messages, dateRange) => {
        // Add debug logging
        clog('getNonRecurringEvents called - input changed');

        // Filter non-recurring messages
        const nonRecurringMessages = messages.filter(message => !message.isRecurring);

        // Filter messages that fall within the date range
        const events = nonRecurringMessages.filter(message => {
            const dueDate = new Date(message.dueDate ?? 0).getTime();
            return dueDate >= dateRange.rangeStartTime && dueDate <= dateRange.rangeEndTime;
        }).map(message => ({
            ...message,
            messageColor: message.messageColor || defaultEventcolor,
        }));

        clog(`Found ${events.length} non-recurring events`);
        return events;
    }
);

// Helper function for processing recurring events
// This is outside the selector to prevent unnecessary recreations
const processRecurringEvents = (
    recurringMessages: UserMessage[],
    dateRange: ReturnType<typeof getDateRange>
): UserMessage[] => {
    clog(`Processing ${recurringMessages.length} recurring messages`);
    clog(`Date range: ${dateRange.rangeStart.toISOString()} to ${dateRange.rangeEnd.toISOString()}`);

    if (recurringMessages.length === 0) {
        return [];
    }

    const events: UserMessage[] = [];

    // Process each recurring message
    recurringMessages.forEach(message => {
        try {
            if (!message.rruleset) {
                return;
            }

            // Parse the rruleset
            const rruleSet = new RRuleSet();

            // Handle old format
            if (message.rruleset.indexOf("RRULE:") === -1) {
                // Handle old format with recurenceUnit and recurrenceQuantity
                if (message.recurenceUnit) {
                    const ruleset = parseOldUnits(message.recurenceUnit as RecurrenceUnit, message.recurrenceQuantity || 1);
                    ruleset.rrules().forEach(rule => {
                        rruleSet.rrule(rule);
                    });
                } else {
                    // Try to parse as RFC string directly
                    try {
                        const ruleset = toRuleSet(message.rruleset);
                        ruleset.rrules().forEach(rule => {
                            rruleSet.rrule(rule);
                        });
                    } catch (error) {
                        console.error(`Error parsing rruleset: ${error}`);
                    }
                }
            } else {
                // Handle new format
                const lines = message.rruleset.split("\n").filter(line => line.trim() !== "");

                lines.forEach(line => {
                    if (line.startsWith("RRULE:")) {
                        const rruleString = line.substring(6);
                        const rule = RRule.fromString(rruleString);
                        rruleSet.rrule(rule);
                    }
                    // Add support for other rrule components if needed
                });
            }

            // Get occurrences within the date range
            const occurrences = rruleSet.between(
                dateRange.rangeStart,
                dateRange.rangeEnd,
                true
            );

            clog(`Message ${message._id} has ${occurrences.length} occurrences in range`);

            // Create an event for each occurrence
            occurrences.forEach(date => {
                const eventDate = new Date(date);

                // Create a new event based on the original message
                const event = {
                    ...message,
                    dueDate: eventDate,
                    originalId: message._id, // Store the original message ID
                    isRecurringInstance: true, // Mark as a recurring instance
                    messageColor: message.messageColor || defaultEventcolor,
                };

                events.push(event);
            });
        } catch (error) {
            console.error(`Error processing recurring message ${message._id}:`, error);
        }
    });

    clog(`Generated ${events.length} recurring events`);
    return events;
};

// Selector for recurring events
export const getRecurringEvents = createSelector(
    [
        (state: CommonRootState) => getFilteredMessages(state),
        (state: CommonRootState) => getDateRange(state),
    ],
    (messages, dateRange) => {
        // Add debug logging
        clog('getRecurringEvents called - input changed');
        const recurringMessages = messages.filter(message => message.isRecurring);
        clog(`Found ${recurringMessages.length} recurring messages`);

        // Only process if we have recurring messages
        if (recurringMessages.length === 0) {
            return [];
        }

        const events = processRecurringEvents(recurringMessages, dateRange);
        clog(`Generated ${events.length} recurring events`);
        return events;
    }
);

// Main selector that combines non-recurring and recurring events
// export const getCalendarEvents = createSelector(
//     [
//         (state: CommonRootState) => getNonRecurringEvents(state),
//         (state: CommonRootState) => getRecurringEvents(state),
//         (state: CommonRootState) => getIsMessagesLoading(state)
//     ],
//     (nonRecurringEvents, recurringEvents, isLoading) => {
//         // Add performance profiling
//         const startTime = performance.now();
//         clog('getCalendarEvents result function called - inputs changed, loading:', isLoading);

//         // If messages are loading, return empty array
//         if (isLoading) {
//             clog('Messages are loading, returning empty events array');
//             return [];
//         }

//         // Combine all events
//         const events = [...nonRecurringEvents, ...recurringEvents];

//         // Log performance metrics
//         const endTime = performance.now();
//         clog(`getCalendarEvents execution time: ${endTime - startTime}ms for ${events.length} events`);

//         return events;
//     }
// );

// Every read goes through normalizePlannerFilters, so an old-shape or partial value can never
// read a missing toggle as "off". Memoised on the stored object so the normalised result keeps
// its identity between renders (a fresh object each call would bust every selector below).
const getFilters = createSelector(
    [(state: CommonRootState) => state.CalendarReducer.calendarSidebarFilters],
    (raw) => normalizePlannerFilters(raw),
);
const getSelectedUser = (state: CommonRootState) => selectUserObject(state);

// FE#338 / planner-filter parity — the raw statuses the calendar SELECTS out of the
// store. This is a fetch/selection bound only; `messageMatchesPlannerFilters` is the
// authority on what actually renders, and the sidebar selects the SAME set, so the two
// surfaces can no longer drift. See @common/services/planner-filters for the semantic
// rule (unresolved is always visible; only resolved history waits for "show past").
export const getUserMessagesForPlanner = createSelector(
    [
        (state: CommonRootState) => state.DBUserMessageReducer,
        getFilters,
    ],
    (DBUserMessageReducer, filters) => selectPlannerMessages(DBUserMessageReducer, filters)
);

/** The source query (derived from the filters) AND the predicate, in one place — so the two
 *  can never be applied separately and disagree. The campaign-child rule lives in the shared
 *  predicate too, so this does not strip `campaignId` rows up front.
 *
 *  No `deleted` clause here on purpose: a send that went out IS a deleted row (the backend
 *  soft-deletes every delivered send), so the predicate — `isSentRecord` — is the one place
 *  that decides which deleted rows are history and which are cancellations. */
function selectPlannerMessages(DBUserMessageReducer: CommonRootState['DBUserMessageReducer'], filters: PlannerFilters): UserMessage[] {
    const messages: UserMessage[] = userMessagesSelectByQuery(DBUserMessageReducer, {
        status: { $in: plannerSourceStatuses(filters) },
    });
    return messages.filter(message => messageMatchesPlannerFilters(message, filters));
}


// Extract date range calculation - hardcoded 1 day before for edge day inclusion
function calculateDateRange(viewMonthStartStr: string, calendarStartDayFromDB: number) {
    const monthStart = moment(viewMonthStartStr).startOf("month");
    const monthEnd = moment(viewMonthStartStr).endOf("month");

    const diffStart = (monthStart.day() - calendarStartDayFromDB + 7) % 7;
    // Hardcode 1 extra day before to ensure edge day events are included
    const rangeStart = monthStart.clone().subtract(diffStart + 1, "days").utc().toDate();

    const lastDayOfWeek = (calendarStartDayFromDB + 6) % 7;
    const diffEnd = (lastDayOfWeek - monthEnd.day() + 7) % 7;
    const rangeEnd = monthEnd.clone().add(diffEnd, "days").utc().toDate();

    return { rangeStart, rangeEnd };
}

// Extract color assignment logic (lines 78-93)
function assignMessageColor(message: UserMessage): UserMessage {
    if (message.calendarColors) {
        const { defaultColor, ranges, specific } = message.calendarColors;

        const dueDateIso = message.dueDate ? new Date(message.dueDate).toISOString() : undefined;
        const specificColor = specific?.find((s) => s.messageTime === dueDateIso);
        if (specificColor) return { ...message, messageColor: specificColor.color };

        const sorted = [...(ranges ?? [])]?.sort((a, b) =>
            moment(a.fromTime).isBefore(moment(b.fromTime)) ? 1 : -1
        );
        const rangeColor = sorted?.find((s) =>
            moment(s.fromTime).isSameOrBefore(moment(message.dueDate))
        );
        if (rangeColor) return { ...message, messageColor: rangeColor.color };

        if (defaultColor) return { ...message, messageColor: defaultColor };
    }
    return { ...message, messageColor: defaultEventcolor };
}


// Every filter decision happens once, inside getUserMessagesForPlanner via the shared
// predicate — the sidebar LIST reads that too. The CALENDAR draws the same set with one
// difference in HOW: a campaign's sends are never drawn one by one, they are folded into
// that campaign's chip (getFilteredCampaigns / getCampaignFailureFolds). Before a campaign
// goes out the calendar shows one chip for it; after, the same chip, marked — "the same
// calendar, the same number of events".
const getFilteredMessagesForCalendarEvents = createSelector(
    [getUserMessagesForPlanner],
    (messages): UserMessage[] => messages.filter(message => !(message as { campaignId?: unknown }).campaignId),
);

/** Every campaign's failed sends, folded per campaign — what its chip says did not go out. */
export const getCampaignFailureFolds = createSelector(
    [(state: CommonRootState) => state.DBUserMessageReducer],
    (DBUserMessageReducer) => foldCampaignFailures(
        userMessagesSelectByQuery(DBUserMessageReducer, { campaignId: { $exists: true } }) as UserMessage[],
    ),
);

// RRule processing cache to avoid expensive recalculations
// Key: messageId + rruleset + rangeStart + rangeEnd
// Value: array of occurrence dates
const rruleOccurrenceCache = new Map<string, Date[]>();
const MAX_CACHE_SIZE = 500; // Limit cache size to prevent memory issues

// Helper to generate cache key
function getRRuleCacheKey(messageId: string, rruleset: string, rangeStart: Date, rangeEnd: Date): string {
    return `${messageId}|${rruleset}|${rangeStart.getTime()}|${rangeEnd.getTime()}`;
}

// Helper to process a single recurring message with caching
function processRecurringMessage(
    m: UserMessage,
    rangeStart: Date,
    rangeEnd: Date
): Date[] {
    const cacheKey = getRRuleCacheKey(m._id, m.rruleset || '', rangeStart, rangeEnd);

    // Check cache first
    const cached = rruleOccurrenceCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    let originalRuleset: RRuleSet;
    try {
        originalRuleset = m.rruleset
            ? toRuleSet(m.rruleset)
            : parseOldUnits(m.recurenceUnit as RecurrenceUnit, m.recurrenceQuantity ?? 0);
    } catch (error) {
        console.error('Failed to parse rruleset in getRecurringMessages:', m.rruleset, error);
        return [];
    }

    // The DTSTART/EXDATE/DST/already-sent rules live in recurring-occurrence-expansion
    // so the display path is unit-testable beside the write path (RRuleRecurrenceHandler);
    // the two disagreeing is what every recurrence bug here has turned out to be.
    const occurrences = expandRecurringOccurrences(originalRuleset, m.dueDate ?? null, rangeStart, rangeEnd);

    // Cache cleanup if too large
    if (rruleOccurrenceCache.size > MAX_CACHE_SIZE) {
        // Remove oldest entries (first 100)
        const keysToDelete = Array.from(rruleOccurrenceCache.keys()).slice(0, 100);
        keysToDelete.forEach(key => rruleOccurrenceCache.delete(key));
    }

    // Store in cache
    rruleOccurrenceCache.set(cacheKey, occurrences);

    return occurrences;
}

/** Occurrences of the recurring messages inside the view's range — shared by the visible
 *  chain and the sent-hidden count, so both expand a series identically. */
function expandRecurringMessages(messages: UserMessage[], viewMonthStartStr: string, selectedUser: any): UserMessage[] {
    // Match the grid's week start (moment `dow`, 0 = Sunday); no +1 offset.
    const calendarStartDayFromDB = (selectedUser?.preferences?.calendarStartDay ?? 0) % 7;
    const { rangeStart, rangeEnd } = calculateDateRange(viewMonthStartStr, calendarStartDayFromDB);

    // A rruleset without a FREQ cannot be expanded: the parser would hand back a
    // DEFAULT weekly-from-now rule and the calendar would paint sends nobody
    // scheduled. Those messages fall through to the non-recurring path below
    // instead, where they render once at their own dueDate.
    const recurringMessages = messages.filter(isExpandableSeries);

    // Early exit if no recurring messages
    if (recurringMessages.length === 0) {
        return [];
    }

    // Process all recurring messages with caching
    const recurring: UserMessage[] = [];

    for (const m of recurringMessages) {
        const occurrences = processRecurringMessage(m, rangeStart, rangeEnd);

        for (const d of occurrences) {
            const due = moment(d).startOf("minute").toDate();
            recurring.push({ ...m, dueDate: due });
        }
    }

    return recurring.map(assignMessageColor);
}

/**
 * Is this a live series to expand into occurrences? A SENT occurrence of a series is not:
 * when an occurrence goes out the backend creates the next one as its own document and
 * soft-deletes the one that sent, which keeps the series' rruleset. Expanding that record
 * would redraw the whole series from it; it is ONE send, drawn where it went out — which is
 * exactly where the series drew it while it was upcoming.
 */
function isExpandableSeries(message: UserMessage): boolean {
    return !!message.isRecurring && hasUsableRecurrenceRule(message.rruleset) && !isSentRecord(message);
}

/** The one-off messages inside the view's range (see expandRecurringMessages). */
function nonRecurringInRange(messages: UserMessage[], viewMonthStartStr: string, selectedUser: any): UserMessage[] {
    // Match the grid's week start (moment `dow`, 0 = Sunday); no +1 offset.
    const calendarStartDayFromDB = (selectedUser?.preferences?.calendarStartDay ?? 0) % 7;
    const { rangeStart, rangeEnd } = calculateDateRange(viewMonthStartStr, calendarStartDayFromDB);

    return messages
        .filter(message => !isExpandableSeries(message))
        .filter(message => moment(message.dueDate).isBetween(rangeStart, rangeEnd, undefined, '[]'))
        .map(v => ({ ...v, messageColor: v.messageColor || defaultEventcolor }));
}

const getRecurringMessages = createSelector(
    [
        getFilteredMessagesForCalendarEvents,
        getViewMonthStartStr,
        getSelectedUser
    ],
    expandRecurringMessages,
);

const getNonRecurringMessages = createSelector(
    [getFilteredMessagesForCalendarEvents, getViewMonthStartStr, getSelectedUser],
    nonRecurringInRange,
);

// FE#240: expand a recurring campaign's RRULE into one date per occurrence in
// [rangeStart, rangeEnd], reusing the SAME machinery recurring MESSAGES use
// (DTSTART/exdate/DST/already-sent filtering) so campaigns and messages render
// identically. The BE only ever materializes ONE future occurrence at a time
// (the parentCampaignId chain), so client-side expansion is the only way to show
// the cadence ahead of time. A campaign stores rruleset/dueDate under `schedule.*`
// (messages hold them at the top level), so we shim to the message shape.
export function campaignOccurrenceDates(
    schedule: { rruleset?: string; dueDate?: string | Date; isRecurring?: boolean } | undefined,
    campaignId: string,
    rangeStart: Date,
    rangeEnd: Date,
): Date[] {
    if (!schedule?.isRecurring || !schedule?.rruleset) return [];
    const occurrences = processRecurringMessage(
        { _id: campaignId, rruleset: schedule.rruleset, dueDate: schedule.dueDate } as unknown as UserMessage,
        rangeStart,
        rangeEnd,
    );
    return occurrences.map(d => moment(d).startOf('minute').toDate());
}

const EMPTY_HISTORY: Record<string, Campaign> = {};

const getFilteredCampaigns = createSelector(
    [
        (state: CommonRootState) => state.DBCampaignReducer,
        getFilters,
        getViewMonthStartStr,
        getSelectedUser,
        (state: CommonRootState) => state.DBDripReducer,
        (state: CommonRootState) => state.PlannerSentHistoryReducer?.campaigns ?? EMPTY_HISTORY,
        getCampaignFailureFolds,
    ],
    (DBCampaignReducer, filters, viewMonthStartStr, selectedUser, DBDripReducer, sentHistory, failures) => {
        const calendarStartDayFromDB = (selectedUser?.preferences?.calendarStartDay ?? 0) % 7;
        const { rangeStart, rangeEnd } = calculateDateRange(viewMonthStartStr, calendarStartDayFromDB);

        // Narrowing filters (group-chats-only) are per-message concepts a campaign cannot
        // satisfy — the sidebar has always hidden campaigns under them, and the calendar
        // agrees. The one exception follows the sidebar too: when the LIST shows a
        // campaign's failed sends (a failed send to a group, under "Groups only"), the
        // calendar still draws the one chip they belong to.
        if (!campaignsVisible(filters)) {
            const knownIds = new Set(mergeCampaignDocs(
                campaignsSelectByQuery(DBCampaignReducer, {}) as Campaign[],
                Object.values(sentHistory ?? EMPTY_HISTORY),
            ).map(c => String(c._id)));
            return [
                ...buildSentCampaignEvents(
                    mergeCampaignDocs(campaignsSelectByQuery(DBCampaignReducer, {}) as Campaign[], Object.values(sentHistory ?? EMPTY_HISTORY)),
                    { filters, rangeStart, rangeEnd, failures, color: defaultEventcolor },
                ),
                ...buildFailedCampaignGroupEvents(failures, knownIds, { filters, rangeStart, rangeEnd, color: defaultEventcolor }),
            ];
        }

        // ONE copy of every campaign the client knows: the live store copy and the one the
        // sent-history load brought in, newer wins. Then each is drawn by exactly one pass —
        // its status decides which — so a step can never be drawn by both, and a step that
        // completes while the page is open moves from this pass to the sent pass on the same
        // render (the live copy is patched, and it is newer than any history copy).
        const known = mergeCampaignDocs(
            campaignsSelectByQuery(DBCampaignReducer, {}) as Campaign[],
            Object.values(sentHistory ?? EMPTY_HISTORY),
        );
        const campaigns = known.filter(c =>
            // 'paused' included so a paused campaign still shows on the calendar — rendered as
            // an inactive chip (isPausedCampaignEvent + CustomMonthEvent) so the user sees it
            // sits in their schedule without mistaking it for a live send.
            ['pending', 'running', 'paused'].includes(String(c.campaignStatus)));
        const sentCampaigns = known.filter(c => campaignOutcomeOf(c.campaignStatus) === 'sent');

        // DUPLICATE-CHIP GUARD — a drip can have MORE THAN ONE materialized step campaign in
        // the store at once (the backend pre-materializes several steps ahead). Each of them
        // used to project the drip's WHOLE remaining sequence from its own dueDate, so a shared
        // future step was drawn once per loaded step campaign — and, because those anchors drift
        // by seconds/minutes, at slightly different times (the 18:10 / 18:11 / 18:14 duplicate
        // chips on the live calendar). So: (a) project follow-ups ONCE per drip, from the LATEST
        // materialized step (the freshest anchor), and (b) never project a step that is already
        // materialized as its own campaign event.
        const materializedStepsByDrip = new Map<string, Set<number>>();
        const followupAnchorByDrip = new Map<string, { campaignId: string; stepIndex: number; dueMs: number }>();
        for (const c of campaigns) {
            if (!c.dripId) continue;
            const did = String(c.dripId);
            const stepIndex = typeof c.dripStepIndex === 'number' ? c.dripStepIndex : 0;
            const dueMs = c.schedule?.dueDate ? new Date(c.schedule.dueDate).getTime() : Number.NEGATIVE_INFINITY;

            let steps = materializedStepsByDrip.get(did);
            if (!steps) { steps = new Set<number>(); materializedStepsByDrip.set(did, steps); }
            steps.add(stepIndex);

            const current = followupAnchorByDrip.get(did);
            const isLater = !current
                || stepIndex > current.stepIndex
                || (stepIndex === current.stepIndex && dueMs > current.dueMs);
            if (isLater) followupAnchorByDrip.set(did, { campaignId: String(c._id), stepIndex, dueMs });
        }

        const events: any[] = [];
        // FE#341 — drips whose STEP CAMPAIGN is loaded here are rendered by the campaign loop
        // below (accurate, real ids). Record them + their current-step dueDate so the drip pass
        // that follows does not double-render them, and can anchor a running drip precisely.
        const dripIdsWithLoadedStep = new Set<string>();
        const stepDueByDripId = new Map<string, any>();
        const stepDuesByDripId = new Map<string, { stepIndex: number; dueMs: number }[]>();
        // #592c / F4 — a single-step skip records the step index on the drip's
        // `skippedSteps` (CURRENT cycle; `skippedSteps` never holds a future
        // cycle's index — those go to `pendingStepSkips`, handled by the recurring
        // pass below). The skipped step's REAL, materialized campaign chip is
        // drawn by the base loop just below, so it must be dropped THERE — the
        // recurring pass's own current-cycle filter is dead for a materialized
        // occurrence (it `continue`s the whole occurrence first). Build a
        // dripId → skipped-step-index set once. (Future cycles aren't materialized,
        // so a materialized step whose index is skipped is always the current one.)
        const skippedStepsByDripId = new Map<string, Set<number>>();
        for (const d of dripsSelectByQuery(DBDripReducer, { deleted: { $ne: true } }) as Array<{ _id?: unknown; skippedSteps?: number[] }>) {
            const did = String(d?._id ?? '');
            const skipped = Array.isArray(d?.skippedSteps) ? d.skippedSteps : [];
            if (did && skipped.length) skippedStepsByDripId.set(did, new Set(skipped));
        }
        for (const c of campaigns) {
            const schedule = c.schedule;
            if (c.dripId) {
                const did = String(c.dripId);
                dripIdsWithLoadedStep.add(did);
                if (!stepDueByDripId.has(did)) stepDueByDripId.set(did, schedule?.dueDate);
                // DUPLICATE-CHIP GUARD (recurring half) — keep EVERY loaded step campaign's send
                // time, not just the first: the recurring pass below identifies "the occurrence
                // already materialized" by matching one of these against the occurrence's own
                // projected step times.
                const dueMsForDrip = schedule?.dueDate ? new Date(schedule.dueDate).getTime() : NaN;
                if (!Number.isNaN(dueMsForDrip)) {
                    const dues = stepDuesByDripId.get(did) ?? [];
                    dues.push({ stepIndex: typeof c.dripStepIndex === 'number' ? c.dripStepIndex : 0, dueMs: dueMsForDrip });
                    stepDuesByDripId.set(did, dues);
                }
            }
            if (schedule?.isRecurring && schedule?.rruleset) {
                // One calendar entry per cadence date (mirrors getRecurringMessages).
                for (const due of campaignOccurrenceDates(schedule, String(c._id), rangeStart, rangeEnd)) {
                    events.push({
                        ...c,
                        dueDate: due,
                        isRecurring: true,
                        // The lean-event builder reads schedule.dueDate first, so the
                        // occurrence date must live there too.
                        schedule: { ...schedule, dueDate: due },
                        messageColor: defaultEventcolor,
                    });
                }
            } else {
                // FE#341 — show a campaign's base (materialized-step) occurrence only when it is an
                // UPCOMING send: a pending campaign (queued), or any campaign whose send is still in
                // the future. A running/completed campaign whose send is in the PAST already went out —
                // it is not "upcoming" and must not clutter the calendar as one. (A running drip's
                // FUTURE follow-up steps are still expanded below.)
                const dueMs = schedule?.dueDate ? new Date(schedule.dueDate).getTime() : undefined;
                // #592c / F4 — this materialized drip-step campaign belongs to a
                // step the user skipped for the current cycle: drop its chip (the
                // send is aborted / suppressed) so the calendar matches the card's
                // "Skipped" state. Bookkeeping above still ran, so the recurring
                // pass still treats this occurrence as materialized (no re-draw).
                const skipStepIdx = typeof c.dripStepIndex === 'number' ? c.dripStepIndex : 0;
                const isSkippedStepChip = !!c.dripId && (skippedStepsByDripId.get(String(c.dripId))?.has(skipStepIdx) ?? false);
                // A RUNNING campaign is in flight — it stays drawn until it resolves, the way a
                // message that is `sending` does. Dropping it at its due time left a hole
                // between "upcoming" and "sent" exactly while it was going out.
                if (!isSkippedStepChip && (c.campaignStatus === 'running' || isUpcomingCampaignSend(c.campaignStatus, dueMs, Date.now()))) {
                    events.push({
                        ...c,
                        dueDate: schedule?.dueDate,
                        isRecurring: schedule?.isRecurring,
                        messageColor: defaultEventcolor,
                    });
                }
            }

            // BE#313 — a multi-message FOLLOW-UP campaign is a DRIP: the backend materializes only
            // the CURRENT step as this Campaign doc (the single event above), while the remaining
            // messages live in `drip.steps[]` and were NEVER rendered — so follow-up messages 2..N
            // were invisible on the calendar and un-editable. Expand each remaining step into its own
            // calendar entry at its projected send time, with its own content + drip/step linkage
            // (so a click can open that step for editing). Recurring drips are handled above.
            // Only the LATEST materialized step of a drip projects its follow-ups (see the
            // duplicate-chip guard above) — every other loaded step campaign renders only itself.
            if (c.dripId && !(schedule?.isRecurring && schedule?.rruleset)
                && followupAnchorByDrip.get(String(c.dripId))?.campaignId === String(c._id)) {
                const drip = dripsSelectOneObjectByQuery(DBDripReducer, { _id: c.dripId }) as any;
                const steps = drip?.steps;
                if (Array.isArray(steps) && steps.length > 1) {
                    const currentIndex = typeof c.dripStepIndex === 'number' ? c.dripStepIndex : 0;
                    const base = resolveBaseSendAt({
                        campaignDueDate: schedule?.dueDate,
                        dripDueDate: drip?.schedule?.dueDate,
                        startedAt: drip?.startedAt,
                    });
                    for (const p of projectFollowupSendDates(base.at, steps, currentIndex)) {
                        // Skip every step that is materialized as its OWN campaign doc (the current
                        // step plus any other pre-materialized step of this drip) — those are real
                        // campaign events emitted above; re-projecting them duplicates the chip.
                        if (materializedStepsByDrip.get(String(c.dripId))?.has(p.stepIndex)) continue;
                        const step = steps.find((s: any) => s?.stepIndex === p.stepIndex);
                        if (!step) continue;
                        // FE#341 — same upcoming rule per follow-up: a FUTURE step always renders;
                        // an already-sent (concretely past) step drops out. Uses THIS step's own
                        // projected send time, so future steps of a running drip still spread.
                        if (!isUpcomingCampaignSend(c.campaignStatus, p.at.getTime(), Date.now())) continue;
                        events.push({
                            _id: `${String(c.dripId)}:step:${p.stepIndex}`,
                            // Shaped like a campaign event (has `schedule`/`template`) so the lean-event
                            // builder + calendar treat it as a campaign occurrence.
                            schedule: { dueDate: p.at },
                            dueDate: p.at,
                            isRecurring: false,
                            template: step.template,
                            name: c.name,
                            // FE#332 — a follow-up occurrence has no audience of its own; it
                            // targets the parent drip's audience. Carry it so the calendar can
                            // name the target groups (fall back to the campaign's own array).
                            boardIDArray: drip?.boardIDArray ?? c.boardIDArray,
                            campaignStatus: c.campaignStatus,
                            // BE#313 linkage — a follow-up occurrence knows its drip + step so the Edit
                            // path can target THIS step (not just step 0).
                            dripId: c.dripId,
                            dripStepIndex: p.stepIndex,
                            isFollowupStep: true,
                            parentCampaignId: c._id,
                            messageColor: defaultEventcolor,
                        });
                    }
                }
            }
        }

        // FE#341 — DRIP-DOC pass. The campaigns service HIDES drip-step campaigns from any find
        // that doesn't name `dripId`, and the calendar's step-campaign fetch is best-effort — so a
        // future scheduled drip's step campaign is usually absent from the store and the loop above
        // rendered nothing for it (while the LIST, reading drip docs, showed it). Render every
        // active/paused drip's UPCOMING steps straight from its drip doc (reliably loaded), skipping
        // drips already covered by a loaded step campaign above. Fully-past drips project to no
        // upcoming steps → naturally omitted.
        // SENT pass — every campaign that went out, at its own send time, marked (see
        // planner-campaign-sends). Its own pass on purpose: fed into the loop above, a
        // completed step would count as "this drip is covered" and suppress the drip's real
        // upcoming steps below.
        events.push(...buildSentCampaignEvents(sentCampaigns, { filters, rangeStart, rangeEnd, failures, color: defaultEventcolor }));
        const sentStepDuesByDripId = new Map<string, { stepIndex: number; dueMs: number }[]>();
        for (const c of sentCampaigns) {
            if (!c.dripId || !c.schedule?.dueDate) continue;
            const dueMs = new Date(c.schedule.dueDate as any).getTime();
            if (Number.isNaN(dueMs)) continue;
            const list = sentStepDuesByDripId.get(String(c.dripId)) ?? [];
            list.push({ stepIndex: typeof c.dripStepIndex === 'number' ? c.dripStepIndex : 0, dueMs });
            sentStepDuesByDripId.set(String(c.dripId), list);
        }
        // A campaign whose failures are loaded but whose document is not: still ONE chip.
        events.push(...buildFailedCampaignGroupEvents(
            failures,
            new Set(known.map(c => String(c._id))),
            { filters, rangeStart, rangeEnd, color: defaultEventcolor },
        ));

        const now = Date.now();
        const nowDate = new Date(now);
        const allDrips = dripsSelectByQuery(DBDripReducer, { deleted: { $ne: true } }) as any[];
        for (const drip of allDrips) {
            const did = String(drip?._id ?? '');
            if (!did || dripIdsWithLoadedStep.has(did)) continue;      // already rendered above
            if (drip.status !== 'active' && drip.status !== 'paused') continue; // launched, non-terminal
            // A RECURRING drip re-runs the whole sequence on drip.schedule.rruleset — expanded
            // by the dedicated recurring-drip pass below, not projected as a single sequence here.
            if (drip.schedule?.isRecurring && drip.schedule?.rruleset) continue;
            const steps = drip.steps;
            if (!Array.isArray(steps) || steps.length === 0) continue;
            const currentIndex = typeof drip.currentStep === 'number' ? drip.currentStep : 0;
            // No upcoming step campaign loaded: anchor on the last step that WENT OUT, when one is
            // known, instead of the drip's start — see currentStepDueFromCompleted.
            const anchor = stepDueByDripId.get(did)
                ?? currentStepDueFromCompleted(drip, sentStepDuesByDripId.get(did), projectFollowupSendDates);
            for (const o of projectDripCalendarOccurrences(drip, nowDate, anchor)) {
                const step = steps.find((s: any) => s?.stepIndex === o.stepIndex);
                if (!step) continue;
                events.push({
                    _id: `${did}:step:${o.stepIndex}`,
                    schedule: { dueDate: o.at },
                    dueDate: o.at,
                    isRecurring: false,
                    template: step.template,
                    name: drip.name,
                    // FE#332 — the drip's audience, so the calendar can name the target groups.
                    boardIDArray: drip.boardIDArray,
                    campaignStatus: drip.status,
                    // BE#313 linkage — click opens THIS step (routing resolves by dripId).
                    dripId: did,
                    dripStepIndex: o.stepIndex,
                    isFollowupStep: !o.isCurrent,
                    messageColor: defaultEventcolor,
                });
            }
        }

        // FE#332 — RECURRING-DRIP pass. A drip whose SCHEDULE recurs (weekly/etc, "Ends: Never")
        // re-runs the whole sequence on each occurrence, but the recurrence lives on
        // `drip.schedule.rruleset` — NOT on the materialized step campaign (createStepCampaign
        // stores only a single `dueDate`). So the campaign loop above renders only the imminent
        // occurrence and the drip-doc pass projects a single sequence; neither expands the weekly
        // re-runs, so future Wednesdays never showed. Expand the rruleset here into occurrence
        // dates (reusing the SAME machinery recurring campaigns/messages use), and for EACH
        // occurrence project the drip's FULL sequence (step 0 + each follow-up at its gap offset),
        // skipping the current materialized occurrence's whole sequence (already drawn by the
        // campaign loop) so the imminent send isn't double-drawn.
        for (const drip of allDrips) {
            const sched = drip?.schedule;
            if (!sched?.isRecurring || !sched?.rruleset) continue;
            if (drip.status !== 'active' && drip.status !== 'paused') continue;
            const did = String(drip?._id ?? '');
            const steps = drip?.steps;
            if (!did || !Array.isArray(steps) || steps.length === 0) continue;
            // The imminent/materialized occurrence (the loaded step campaign's dueDate) — its
            // WHOLE sequence (step 0 + follow-ups) is already emitted by the campaign loop + its
            // follow-up block; skip the entire occurrence here to avoid double-drawing it.
            // The match is made on ANY of the occurrence's projected step times vs ANY loaded step
            // campaign's dueDate: the backend materializes a MID-sequence step (step N, hours after
            // the occurrence start), so comparing the occurrence START against that step's dueDate
            // never matched — and the current occurrence was drawn a second time on top of its real
            // campaign chips (duplicate chips on the calendar).
            // A materialized step campaign does NOT sit exactly on its projected time: the backend
            // schedules the next step at "when the previous one actually ran + gap", so a busy
            // account drifts minutes off (observed live: 17:12:02 against a projected 17:10:00).
            // So the occurrence is matched by the NEAREST projected time for the SAME step index —
            // bounded by half the cadence, so a step campaign belonging to another occurrence can
            // never swallow this one. (A fixed tolerance missed the drifted ones, and the whole
            // occurrence was then drawn a second time next to its real campaign chips.)
            // A completed step marks its occurrence as taken too: the sent pass draws it where
            // it went out, so re-projecting that occurrence here would draw it twice.
            const loadedSteps = [...(stepDuesByDripId.get(did) ?? []), ...(sentStepDuesByDripId.get(did) ?? [])];
            const occurrences = campaignOccurrenceDates(sched, did, rangeStart, rangeEnd);
            const projectedByOccurrence = occurrences.map(occ => ({ occ, projectedSteps: projectFollowupSendDates(occ, steps, 0) }));

            let cadenceMs = Number.POSITIVE_INFINITY;
            for (let i = 1; i < occurrences.length; i++) {
                cadenceMs = Math.min(cadenceMs, occurrences[i].getTime() - occurrences[i - 1].getTime());
            }
            // A single in-range occurrence gives no measurable cadence — fall back to half a day.
            const matchWindowMs = Number.isFinite(cadenceMs) ? cadenceMs / 2 : 12 * 60 * 60 * 1000;

            const materializedOccurrenceTimes = new Set<number>();
            for (const loaded of loadedSteps) {
                let nearestOcc: Date | undefined;
                let nearestDistance = Number.POSITIVE_INFINITY;
                for (const { occ, projectedSteps } of projectedByOccurrence) {
                    const projected = projectedSteps.find(p => p.stepIndex === loaded.stepIndex);
                    if (!projected) continue;
                    const distance = Math.abs(projected.at.getTime() - loaded.dueMs);
                    if (distance < nearestDistance) { nearestDistance = distance; nearestOcc = occ; }
                }
                if (nearestOcc && nearestDistance <= matchWindowMs) materializedOccurrenceTimes.add(nearestOcc.getTime());
            }

            // #592c — a step skipped for a given occurrence must not be drawn.
            // The CURRENT cycle's `skippedSteps` apply to the occurrence that sits
            // on the drip's own `dueDate`; a `pendingStepSkips` override applies to
            // whichever future occurrence its `occurrenceDate` matches (minute
            // granularity — the BE↔client contract). Both are compared at minute
            // resolution against each projected occurrence's START.
            const dripSkip = drip as { skippedSteps?: number[]; pendingStepSkips?: PendingStepSkipEntry[] };
            const skippedSteps = Array.isArray(dripSkip.skippedSteps) ? dripSkip.skippedSteps : [];
            const dueMinute = sched.dueDate ? Math.floor(new Date(sched.dueDate).getTime() / 60000) : null;
            const pendingStepSkips = Array.isArray(dripSkip.pendingStepSkips) ? dripSkip.pendingStepSkips : [];

            for (const { occ, projectedSteps } of projectedByOccurrence) {
                if (materializedOccurrenceTimes.has(occ.getTime())) continue;
                const occMinute = Math.floor(occ.getTime() / 60000);
                // Project the FULL sequence for THIS occurrence: step 0 at the occurrence date, then
                // each follow-up at its own gap offset from that occurrence's start (same forward
                // walk the campaign follow-up block uses), so a recurring multi-step drip shows every
                // message on every future cadence — not just step 0.
                for (const p of projectedSteps) {
                    // #592c / F4 — hide a step skipped for THIS occurrence: the current
                    // cycle's `skippedSteps` (occ == the drip dueDate) OR a matching
                    // `pendingStepSkips` override (a future cycle). One minute-quantised,
                    // unit-tested predicate owns the decision (recurring-occurrence-filter),
                    // and this pass only reaches NON-materialized occurrences — the current
                    // (materialized) cycle's real chips are filtered in the base loop above.
                    if (isDripStepSkippedForOccurrence({ stepIndex: p.stepIndex, occurrenceMinute: occMinute, dueMinute, skippedSteps, pendingStepSkips })) continue;
                    const step = steps.find((s: any) => s?.stepIndex === p.stepIndex);
                    if (!step) continue;
                    events.push({
                        _id: `${did}:occ:${occ.getTime()}:step:${p.stepIndex}`,
                        // #592b — mark recurring so the lean event's `isRecurring` is
                        // true (the chip's per-date skip affordance keys off it).
                        schedule: { dueDate: p.at, isRecurring: true },
                        dueDate: p.at,
                        isRecurring: true,
                        // #592b — the OCCURRENCE START (the rrule instant), NOT p.at (a
                        // follow-up step's shifted time). This is the exact value the
                        // per-date skip writes as an EXDATE, so BE + client agree.
                        dripOccurrenceStartMs: occ.getTime(),
                        template: step.template,
                        name: drip.name,
                        // FE#332 — the drip's audience, so the calendar can name the target groups.
                        boardIDArray: drip.boardIDArray,
                        campaignStatus: drip.status,
                        dripId: did,
                        dripStepIndex: p.stepIndex,
                        isFollowupStep: p.stepIndex !== 0,
                        messageColor: defaultEventcolor,
                    });
                }
            }
        }

        // Paused-DRIP stamp. A drip's paused state lives on the DRIP (`drip.status`), NOT on its
        // materialized step campaigns — those stay `pending`. So a paused drip's occurrences would
        // otherwise read as live. Mark every event belonging to a paused drip as `paused` so the chip
        // dims (isPausedCampaignEvent). Covers base drip-step events here AND any drip follow-up /
        // drip-doc events a merged FE#341 adds — they all carry `dripId`. A paused STANDALONE campaign
        // is already `paused` from its own status; this only adds the drip case.
        markPausedDripEvents(events, pausedDripIdSet(dripsSelectByQuery(DBDripReducer, { status: 'paused' }) as any[]));

        return events;
    }
);


const getCurrentView = (state: CommonRootState) => state.CalendarReducer.view;

/** The marks a campaign chip carries — stamped by the sent pass, or derived for an upcoming
 *  chip (a projected step has no document of its own and so no sends of its own). */
function pickCampaignMarks(e: any, failures: ReturnType<typeof foldCampaignFailures>) {
    const stamped = e.effectiveStatus || e.recipientSplit || e.failedCount;
    const marks = stamped
        ? { effectiveStatus: e.effectiveStatus, recipientSplit: e.recipientSplit, failedCount: e.failedCount }
        : campaignChipOutcome(e, typeof e._id === 'string' && !e._id.includes(':step:') ? failures.get(e._id) : undefined);
    return {
        ...(marks.effectiveStatus ? { effectiveStatus: marks.effectiveStatus } : {}),
        ...(marks.recipientSplit ? { recipientSplit: { sent: marks.recipientSplit.sent, total: marks.recipientSplit.total } } : {}),
        ...(marks.failedCount ? { failedCount: marks.failedCount } : {}),
    };
}

export const getCalendarEvents = createSelector(
    [getNonRecurringMessages, getRecurringMessages, getFilteredCampaigns, getCurrentView],
    (nonRecurring, recurring, campaigns, currentView) => {
        const allEvents = [...nonRecurring, ...recurring, ...campaigns];

        return allEvents;
    }
);

// An event whose content is unchanged keeps its previous object, so editing one
// message re-renders one chip, not the whole month (see stable-lean-events.ts).
const reuseUnchangedLeanEvents = createLeanEventReuser<any>();

// Enriched lean events selector - includes all display data to avoid per-event selector calls
// This is a major performance optimization: instead of N selector calls for N events,
// we compute everything once in this selector
export const getCalendarLeanEvents = createSelector(
    [getNonRecurringMessages, getRecurringMessages, getFilteredCampaigns, getCurrentView, (state: CommonRootState) => state.DBBoardsReducer, getCampaignFailureFolds],
    (nonRecurring, recurring, campaigns, currentView, DBBoardsReducer, failures) => {
        const allEvents = [...nonRecurring, ...recurring, ...campaigns];

        // FE#332 — resolve audience board ids → group names ONCE here (the chip
        // must not do per-event board lookups; see CustomMonthEvent's N+1 note).
        const boardNameMap = buildBoardNameMap(
            boardsSelectByQuery(DBBoardsReducer, { type: 'audience', deleted: { $ne: true } }) as any[],
        );

        return reuseUnchangedLeanEvents(allEvents.map((e: any) => {
            const isCampaign = 'campaignStatus' in e || 'schedule' in e;

            if (isCampaign) {
                // FE#332 — target group NAMES for the calendar hover tooltip.
                const audienceGroupNames = resolveGroupNames(e.boardIDArray, boardNameMap);
                const audienceGroupCount = Array.isArray(e.boardIDArray) ? e.boardIDArray.length : 0;
                // Campaign event - include template data for display
                return {
                    _id: e._id,
                    isCampaign: true,
                    // Source status → the chip dims + de-colors a PAUSED campaign (isPausedCampaignEvent).
                    campaignStatus: e.campaignStatus,
                    dueDate: e.schedule?.dueDate ?? e.dueDate,
                    isRecurring: e.schedule?.isRecurring ?? false,
                    // Display data for CustomMonthEvent
                    message: e.template?.message ?? e.name ?? '',
                    messageColor: 'var(--calendar-color-sage)',
                    asset: e.template?.asset ? {
                        url: e.template.asset.url,
                        type: e.template.asset.type
                    } : null,
                    contactList: null,
                    isPoll: false,
                    poll: null,
                    // BE#313 — carry the drip/step linkage so a click on a FOLLOW-UP occurrence can
                    // open that specific step for editing (the current step keeps the campaign menu).
                    dripId: e.dripId,
                    dripStepIndex: e.dripStepIndex,
                    isFollowupStep: e.isFollowupStep === true,
                    // #592b — occurrence start for the per-date skip (recurring drip
                    // occurrences only); undefined on materialized/campaign events.
                    dripOccurrenceStartMs: e.dripOccurrenceStartMs,
                    parentCampaignId: e.parentCampaignId,
                    // FE#332 — hover tooltip reveals WHICH groups this event targets.
                    audienceGroupNames,
                    audienceGroupCount,
                    // "The same chip, marked": what went out and what did not. The sent pass
                    // stamps these; an upcoming chip gets its failures-so-far here.
                    ...pickCampaignMarks(e, failures),
                };
            } else {
                // User message event - include all display data
                return {
                    _id: e._id,
                    isCampaign: false,
                    dueDate: e.dueDate,
                    isRecurring: e.isRecurring ?? false,
                    // Display data for CustomMonthEvent
                    message: e.message ?? '',
                    messageColor: e.messageColor ?? defaultEventcolor,
                    asset: e.asset ? {
                        url: e.asset.url,
                        type: e.asset.type
                    } : null,
                    // Only include first contact for avatar display
                    contactList: e.contactList?.slice(0, 1) ?? null,
                    isPoll: e.isPoll ?? false,
                    poll: e.isPoll ? { question: e.poll?.question } : null,
                    rruleset: e.rruleset,
                    // FE#338 — effective status for the past/sent/failed badge in
                    // CustomMonthEvent. Undefined for a plain upcoming 'pending' row
                    // (no badge); 'expired'/'unconfirmed'/'failed'/etc. once it ran.
                    // Reuses the single source of truth so the calendar badge matches
                    // the sidebar/detail treatment exactly.
                    effectiveStatus: getEffectiveMessageStatus(e),
                    // The record of a send that went out — drawn, never re-planned.
                    ...(isSentRecord(e) ? { isSentRecord: true } : {}),
                };
            }
        }));
    }
);


export const shouldGetMoreContactsSelector = createSelector(
    [
        (state: CommonRootState) => state,
    ],
    (state: CommonRootState) => {
        const isSockectConnected = getIsSocketConnectedToWhatsApp(state)
        const totalContacts = state.DBWaContactsCacheReducer.total

        return !(!isSockectConnected && totalContacts === 0)
    }
)

// Memoized selectors
export const selectFilteredContacts = createSelector(
    [(state: CommonRootState) => state.DBWaContactsCacheReducer.entities, (_, filter: string) => filter],
    (entities, filter) => {
        const contacts = Object.values(entities) as WaContact[];
        if (!filter) return contacts;

        const lowerFilter = filter.toLowerCase();
        return contacts.filter(contact => {
            if (!contact) return false;

            const searchableFields = [
                contact.name,
                contact.id?.user,
                contact.pushname,
                contact.shortName
            ].map(field => field?.toLowerCase() ?? '');

            return searchableFields.some(field => field.includes(lowerFilter));
        });
    }
);

// Type for minimal contact data needed for rendering
export interface MinimalContact {
    _id?: string;
    id: { _serialized: string };
    name?: string;
    pushname: string;
    shortName?: string;
    isGroup: boolean;
    verifiedName?: undefined;
    imgUrl?: string;
    profilePicLastUpdate?: string;
    _isChat?: boolean;
    /** #294: archived chat (drives the picker's "Archived" marker). */
    archived?: boolean;
}

// Optimized selector that returns only fields needed for rendering
// skipSort: When true, preserves Redux insertion order (for server-side pagination)
//           When false (default), sorts alphabetically (for extension mode with all contacts)
export const selectFilteredContactsMinimal = createSelector(
    [
        (state: CommonRootState) => state.DBWaContactsCacheReducer.entities,
        (state: CommonRootState) => state.DBWaContactsCacheReducer.ids, // Use ids array to preserve insertion order
        (_, filter: string) => filter,
        (_, _filter: string, skipSort: boolean = false) => skipSort,
        (_, _filter: string, _skipSort: boolean = false, typeFilter: string = 'all') => typeFilter
    ],
    (entities, ids, filter, skipSort, typeFilter) => {
        // Use ids array to get contacts in insertion order (preserves pagination order)
        const contacts = (ids || []).map((id: string | number) => entities[id]).filter(Boolean) as WaContact[];

        const mapToMinimal = (contact: WaContact): MinimalContact => {
            // Extract serialized ID from various possible locations
            // PageMessageService sets waId, while direct WhatsApp contacts have id._serialized
            // Database stores _id as the serialized ID (e.g., "972xxx@c.us")
            const serializedId = contact?.id?._serialized ||
                                (contact as any)?.waId ||
                                contact?._serialized ||
                                contact?._id; // Fallback to _id which is the serialized ID in DB
            return {
                _id: contact?._id,
                id: { _serialized: serializedId },
                name: contact?.name,
                pushname: contact?.pushname,
                shortName: contact?.shortName,
                isGroup: contact?.isGroup,
                verifiedName: contact?.verifiedName,
                imgUrl: contact?.imgUrl,
                profilePicLastUpdate: (contact as any)?.profilePicLastUpdate,
                _isChat: (contact as any)?._isChat,
                archived: (contact as any)?.archived,
            };
        };

        const minimalContacts = contacts.map(mapToMinimal);

        // In server mode (skipSort=true), preserve insertion order from pagination
        // In extension mode (skipSort=false), sort alphabetically
        const orderedContacts = skipSort
            ? minimalContacts
            : sortContactsByGroupThenName(minimalContacts);

        let result = orderedContacts;

        // Apply type filter
        if (typeFilter && typeFilter !== 'all') {
            result = result.filter(contact => {
                const serialized = contact?.id?._serialized || '';
                switch (typeFilter) {
                    case 'contacts':
                        return serialized.endsWith('@c.us') || serialized.endsWith('@lid');
                    case 'groups':
                        return chatKindOf(serialized) === 'group';
                    case 'channels':
                        return chatKindOf(serialized) === 'channel';
                    default:
                        return true;
                }
            });
        }

        if (!filter) {
            return result;
        }

        const lowerFilter = filter.toLowerCase();
        return result.filter(contact => {
            if (!contact) return false;

            const searchableFields = [
                contact.name,
                contact.id?._serialized?.split('@')[0], // Match extraction in sort
                contact.id?._serialized, // Full chat ID (e.g. 972548275828@c.us, 120363XXX@newsletter)
                contact.pushname,
                contact.shortName
            ].map(field => field?.toLowerCase() ?? '');

            return searchableFields.some(field => field.includes(lowerFilter));
        });
    }
);

export const getWhatsappContactNameById = createSelector(
    [
        (state: CommonRootState, whatsappId, _contactName) => state,
        (_, whatsappId: string, _contactName) => whatsappId,
        (_, _contactName) => _contactName
    ],
    (state: CommonRootState, whatsappId, _contactName) => {
        const whatsappContact = selectWhatsappContactById(state, whatsappId);

        const _displayName = whatsappContact?.displayName ?? whatsappContact?.name ?? ''
        const displayName = _displayName?.indexOf('+') === 0 ? (whatsappContact?.name ?? whatsappContact?.displayName) : _displayName
        const contactName = _contactName ?? displayName;

        return contactName;
    }
);
